import { useCallback, useMemo } from 'react';
import type { Permission } from 'modules/PermissionMenu';
import Button from 'elements/Button';
import ManageAccessSkeleton from './ManageAccessSkeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { styles } from './styles';
import PermissionMenu from 'modules/PermissionMenu';
import { Invitation, InvitationRole } from 'models/Invitation';
import useBoxInvitations from 'networking/mindfeed/hooks/useBoxInvitations';
import { Box } from 'models/Box';
import useUpdateBoxInvitationRole from 'networking/mindfeed/hooks/useUpdateBoxInvitationRole';
import useDeleteBoxInvitation from 'networking/mindfeed/hooks/useDeleteBoxInvitation';
import useResendBoxInvitation from 'networking/mindfeed/hooks/useResendBoxInvitation';
import { openBoxModal } from 'xstate/actors/ModalActor';
import { BoxModalStep } from 'xstate/machines/ModalMachine';
import { useTranslations } from 'next-intl';

type Props = {
  box: Box;
};

const EmailInvitations = ({ box }: Props) => {
  const t = useTranslations();
  const { data: allInvitations, isLoading } = useBoxInvitations({
    boxId: box.id,
  });

  const invitations = useMemo(
    () =>
      allInvitations
        // only email invitations
        ?.filter((inv) => inv.method === 'email' && inv.expiresAt)
        // Sort by createdAt ascending
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        ) ?? [],
    [allInvitations],
  );

  const updateBoxInvitationRoleMutation = useUpdateBoxInvitationRole();
  const deleteBoxInvitationMutation = useDeleteBoxInvitation();
  const resendBoxInvitationMutation = useResendBoxInvitation();

  const handlePermissionChange = useCallback(
    async (invitation: Invitation, nextRole: Permission) => {
      // Convert Permission to Invitation role
      const nextInvitationRole: InvitationRole =
        nextRole === 'view' ? 'viewer' : 'editor';

      if (invitation.role === nextInvitationRole) {
        return;
      }

      await updateBoxInvitationRoleMutation.mutateAsync({
        boxId: box.id,
        invitationId: invitation.id,
        role: nextInvitationRole,
      });
    },
    [box, updateBoxInvitationRoleMutation],
  );

  const handleResend = useCallback(
    async (invitation: Invitation) => {
      await resendBoxInvitationMutation.mutateAsync({
        boxId: box.id,
        invitationId: invitation.id,
      });
    },
    [box, resendBoxInvitationMutation],
  );

  const handleRemove = useCallback(
    async (invitation: Invitation) => {
      await deleteBoxInvitationMutation.mutateAsync({
        boxId: box.id,
        invitationId: invitation.id,
      });
    },
    [box, deleteBoxInvitationMutation],
  );

  const handleAdd = useCallback(() => {
    openBoxModal(box, {
      step: BoxModalStep.InvitePeople,
      canNavigateBack: true,
    });
  }, [box]);

  return (
    <>
      <p className={styles.title.emphasised}>{t('boxSharing.inviteByEmail')}</p>

      {isLoading && (
        <div className={styles.skeletons}>
          {Array.from({ length: 3 }).map((_, index) => (
            <ManageAccessSkeleton key={index} variant="invitation" />
          ))}
        </div>
      )}

      {!isLoading && (
        <>
          {invitations.map((invitation) => (
            <div
              className={styles.sections.email.row.container}
              key={invitation.id}
            >
              <div className={styles.sections.email.row.left}>
                <div
                  className={styles.sections.email.row.avatar}
                  aria-hidden="true"
                />
                <div className={styles.sections.email.row.textCol}>
                  <p className={styles.sections.email.row.email}>
                    {invitation.destination}
                  </p>
                  <p className={styles.sections.email.row.pending}>
                    {t('boxSharing.pendingInvitation')}
                  </p>
                </div>
              </div>

              <PermissionMenu
                permission={invitation.role === 'viewer' ? 'view' : 'edit'}
                onChange={(next) => handlePermissionChange(invitation, next)}
                onResend={() => handleResend(invitation)}
                showResend
                onRemove={() => handleRemove(invitation)}
                isLoading={isLoading}
                showRemove
                showChevronUpWhenOpen
                showCanPrefix
              />
            </div>
          ))}

          {/* {isCreatingInvitation && (
            <ManageAccessSkeleton variant="invitation" />
          )} */}
        </>
      )}

      {/* Add Member */}
      <Button
        type="button"
        variant="ghost"
        color="neutral"
        fullWidth
        justifyContent="between"
        noAnimation
        onClick={handleAdd}
        className="mb-6 p-0 min-h-0 rounded-none font-normal whitespace-normal"
      >
        <span className={styles.sections.email.btn.addInvitee.left.container}>
          <span
            className={styles.sections.email.btn.addInvitee.left.icon}
            aria-hidden="true"
          >
            <FontAwesomeIcon className={styles.text.muted} icon={faUserPlus} />
          </span>
          <span className={styles.sections.email.btn.addInvitee.left.text}>
            {t('boxSharing.addPeople')}
          </span>
        </span>

        <FontAwesomeIcon
          className={styles.text.muted}
          icon={faChevronRight}
          aria-hidden="true"
        />
      </Button>
    </>
  );
};

export default EmailInvitations;
